import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap, LogOut, Inbox, TrendingUp, Globe2, CalendarClock,
  Phone, Mail, Trash2, Search, Loader2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["new", "contacted", "in_progress", "converted", "closed"];
const STATUS_LABEL = { new: "New", contacted: "Contacted", in_progress: "In Progress", converted: "Converted", closed: "Closed" };
const STATUS_STYLE = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  in_progress: "bg-purple-100 text-purple-700",
  converted: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-200 text-slate-600",
};

const AXIS_TICK = { fontSize: 12, fill: "#64748b" };
const BAR_CURSOR = { fill: "#f8fafc" };
const BAR_RADIUS = [6, 6, 0, 0];
const HTTP_UNAUTHORIZED = 401;
const CHART_HEIGHT = 240;
const DATE_FORMAT = { day: "2-digit", month: "short", year: "numeric" };

function EnquiryRow({ enquiry, onUpdateStatus, onRemove }) {
  const interest = [enquiry.qualification, enquiry.course].filter(Boolean).join(" · ") || "—";
  return (
    <tr data-testid={`enquiry-row-${enquiry.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <td className="px-5 py-4">
        <div className="font-semibold text-navy">{enquiry.full_name}</div>
        {enquiry.message && <div className="text-xs text-slate-400 max-w-[200px] truncate">{enquiry.message}</div>}
      </td>
      <td className="px-5 py-4">
        <a href={`tel:${enquiry.phone}`} className="flex items-center gap-1.5 text-slate-600 hover:text-navy"><Phone className="w-3.5 h-3.5" /> {enquiry.phone}</a>
        <a href={`mailto:${enquiry.email}`} className="flex items-center gap-1.5 text-slate-600 hover:text-navy mt-1"><Mail className="w-3.5 h-3.5" /> {enquiry.email}</a>
      </td>
      <td className="px-5 py-4">
        <div className="font-medium text-navy">{enquiry.country}</div>
        <div className="text-xs text-slate-400">{interest}</div>
      </td>
      <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(enquiry.created_at).toLocaleDateString("en-IN", DATE_FORMAT)}</td>
      <td className="px-5 py-4">
        <select
          data-testid={`status-select-${enquiry.id}`}
          value={enquiry.status}
          onChange={(ev) => onUpdateStatus(enquiry.id, ev.target.value)}
          className={`rounded-full px-3 py-1.5 text-xs font-bold border-0 outline-none cursor-pointer ${STATUS_STYLE[enquiry.status]}`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </td>
      <td className="px-5 py-4">
        <button data-testid={`delete-${enquiry.id}`} onClick={() => onRemove(enquiry.id)} className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
      </td>
    </tr>
  );
}

function EnquiriesTable({ rows, onUpdateStatus, onRemove }) {
  if (rows.length === 0) {
    return (
      <div className="p-16 text-center text-slate-400" data-testid="empty-state">
        <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
        No enquiries found.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="enquiries-table">
        <thead>
          <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
            <th className="px-5 py-3 font-semibold">Student</th>
            <th className="px-5 py-3 font-semibold">Contact</th>
            <th className="px-5 py-3 font-semibold">Interest</th>
            <th className="px-5 py-3 font-semibold">Date</th>
            <th className="px-5 py-3 font-semibold">Status</th>
            <th className="px-5 py-3 font-semibold"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <EnquiryRow key={e.id} enquiry={e} onUpdateStatus={onUpdateStatus} onRemove={onRemove} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatCards({ stats }) {
  if (!stats) return null;
  const cards = [
    { icon: Inbox, label: "Total Enquiries", value: stats.total, color: "bg-navy" },
    { icon: TrendingUp, label: "New (unactioned)", value: stats.by_status.new, color: "bg-blue-600" },
    { icon: CalendarClock, label: "Last 7 Days", value: stats.last_7_days, color: "bg-gold" },
    { icon: Globe2, label: "Converted", value: stats.by_status.converted, color: "bg-emerald-600" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} data-testid={`stat-${c.label}`} className="bg-white rounded-2xl border border-slate-200 p-5">
          <span className={`grid place-items-center w-11 h-11 rounded-xl ${c.color} text-white mb-3`}><c.icon className="w-5 h-5" /></span>
          <div className="font-display text-3xl font-extrabold text-navy">{c.value ?? 0}</div>
          <div className="text-sm text-slate-500">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

function CountryChart({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <h3 className="font-display font-bold text-navy mb-4">Enquiries by Country</h3>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
          <XAxis dataKey="country" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <Tooltip cursor={BAR_CURSOR} />
          <Bar dataKey="count" fill="#0A1B3F" radius={BAR_RADIUS} maxBarSize={48} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function matchesSearch(enquiry, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    enquiry.full_name.toLowerCase().includes(q) ||
    enquiry.email.toLowerCase().includes(q) ||
    enquiry.phone.includes(q)
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/enquiries${filter !== "all" ? `?status=${filter}` : ""}`),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
    } catch (err) {
      if (err.response?.status === HTTP_UNAUTHORIZED) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    load();
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const filtered = enquiries.filter((e) => matchesSearch(e, search));

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <aside className="hidden md:flex flex-col w-64 bg-navy text-white p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 mb-10">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-gold text-navy"><GraduationCap className="w-6 h-6" /></span>
          <span className="font-display font-extrabold leading-tight">INVICTA<br /><span className="text-gold text-sm">Admin</span></span>
        </div>
        <nav className="space-y-1 flex-1">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 border-l-4 border-gold font-semibold">
            <Inbox className="w-5 h-5" /> Enquiries
          </div>
        </nav>
        <div className="border-t border-white/10 pt-4">
          <div className="text-sm text-white/60 mb-3 px-1 truncate">{user?.email}</div>
          <button data-testid="logout-btn" onClick={doLogout} className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="font-display text-xl font-bold text-navy">Student Enquiries</h1>
            <p className="text-xs text-slate-500">Manage and track all incoming leads</p>
          </div>
          <button onClick={doLogout} className="md:hidden text-navy"><LogOut className="w-5 h-5" /></button>
        </header>

        <div className="p-6 space-y-6">
          <StatCards stats={stats} />
          <CountryChart data={stats?.by_country} />

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {["all", ...STATUSES].map((s) => (
                <button
                  key={s}
                  data-testid={`filter-${s}`}
                  onClick={() => setFilter(s)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${filter === s ? "bg-navy text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-navy"}`}
                >
                  {s === "all" ? "All" : STATUS_LABEL[s]}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone" className="pl-9 pr-4 py-2.5 rounded-full border border-slate-200 text-sm w-full sm:w-64 outline-none focus:border-navy" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-16 text-center"><Loader2 className="w-7 h-7 text-navy animate-spin mx-auto" /></div>
            ) : (
              <EnquiriesTable rows={filtered} onUpdateStatus={updateStatus} onRemove={remove} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
